/**
 *   This is a service for a specific store module.
 *   We separate our files for easy managment and readibility.
 *   We export this as a service to be used in other areas of the application.
 */
import Api from '@/services/api';
import AuthService from '@/services/auth';
import Store from '@/store/store';

export default {

  async errorHandler(message: string) {
    Store.dispatch('app/snackbar', {
      show: true,
      text: message,
      color: 'error',
      icon: Store.getters['app/icons'].error,
    });
  },

  async warningHandler(message: string) {
    Store.dispatch('app/snackbar', {
      show: true,
      text: message,
      color: 'warning',
      icon: Store.getters['app/icons'].warning,
    });
  },

  async successHandler(message: string) {
    Store.dispatch('app/snackbar', {
      show: true,
      text: message,
      color: 'success',
      icon: Store.getters['app/icons'].success,
    });
  },

  async getDrawer() {
    return Store.getters['app/drawer'];
  },

  async setDrawer(val: any) {
    Store.dispatch('app/SET_DRAWER', val);
  },

  async getGenders(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/genders'].length < 1) {
      try {
        const response = await Api().get('/app/list_genders');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setGenders', { genders: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setGenders', {
              genders: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setGenders', { genders: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 73 - getGenders - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getRaces(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/races'].length < 1) {
      try {
        const response = await Api().get('/app/list_races');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setRaces', { races: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setRaces', {
              races: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setRaces', { races: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 109 - getRaces - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getLocations(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/locations'].length < 1) {
      try {
        const response = await Api().get('/app/list_locations_local');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setLocations', { locations: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setLocations', {
              locations: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setLocations', { locations: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 145 - getLocations - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getStatuses(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/statuses'].length < 1) {
      try {
        const response = await Api().get('/app/list_statuses');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setStatuses', { statuses: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setStatuses', {
              statuses: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setStatuses', { statuses: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 181 - getStatuses - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getMarks(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/marks'].length < 1) {
      try {
        const response = await Api().get('/app/list_marks');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setMarks', { marks: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setMarks', {
              marks: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setMarks', { races: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 217 - getMarks - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getSubjects(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/subjects'].length < 1) {
      try {
        const response = await Api().get('/app/list_subjects');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setSubjects', { subjects: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setSubjects', {
              subjects: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setSubjects', { subjects: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 253 - getSubjects - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getQualifications(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/qualifications'].length < 1) {
      try {
        const response = await Api().get('/app/list_qualifications');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setQualifications', { qualifications: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setQualifications', {
              qualifications: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setQualifications', { qualifications: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 289 - getQualifications - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getAspects(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/aspects'].length < 1) {
      try {
        const response = await Api().get('/app/list_aspects');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setAspects', { aspects: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setAspects', {
              aspects: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setAspects', { aspects: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 325 - getAspects - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getCompanies(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/companies'].length < 1) {
      try {
        const response = await Api().get('/app/list_companies');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setCompanies', { companies: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setCompanies', {
              companies: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setCompanies', { companies: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 361 - getCompanies - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getInstitutions(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/institutions'].length < 1) {
      try {
        const response = await Api().get('/app/list_institutions');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setInstitutions', { institutions: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setInstitutions', {
              institutions: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setInstitutions', { institutions: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 397 - getInstitutions - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getCareers(refreshFromAPI: boolean) {
    if (refreshFromAPI || Store.getters['app/careers'].length < 1) {
      try {
        const response = await Api().get('/app/list_careers');
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/resetCareers', { careers: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setCareers', {
              careers: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setCareers', { careers: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 431 - getCareers - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getCareerCategories(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/careerCategories'].length < 1) {
      try {
        const response = await Api().get('/app/list_career_categories');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setCareerCategories', { careerCategories: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setCareerCategories', {
              careerCategories: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setCareerCategories', { careerCategories: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 467 - getCareerCategories - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getCareerSubCategories(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/careerSubCategories'].length < 1) {
      try {
        const response = await Api().get('/app/list_career_sub_categories');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setCareerSubCategories', { careerSubCategories: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setCareerSubCategories', {
              careerSubCategories: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setCareerSubCategories', { careerSubCategories: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 503 - getCareerSubCategories - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getClassifications(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['app/classifications'].length < 1) {
      try {
        const response = await Api().get('/app/list_classifications');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            Store.dispatch('app/setClassifications', { classifications: [] });
            return response.data.message;
          } else {
            Store.dispatch('app/setClassifications', {
              classifications: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        Store.dispatch('app/setClassifications', { classifications: [] });
        if (error.response) {
          return error.response.statusText;
        } else {
          await AuthService.logSupportDebug('app.ts - 539 - getClassifications - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      }
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async getInstitutionsBySubject(config: { params: { subjectId: number } }) {
    return Api().get('/search/institutions_by_subject', config);
  },

  async getInstitutionsByTertiary(config: { params: { searchedTerm: string } }) {
    return Api().get('/search/institutions_by_institutions', config);
  },

  async getInstitutionsByQualification(config: { params: { searchedTerm: string } }) {
    return Api().get('/search/institutions_by_qualification', config);
  },

};
